import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentAdmin } from "@/hooks/useCurrentAdmin";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  Loader2,
  Calendar as CalendarIcon,
  ChevronsUpDown,
  Check,
  ChevronDown,
  X,
  User,
  Building2,
} from "lucide-react";
import { sendAdminTaskAssignedEmail } from "@/lib/emailService";
import { getRoleColor } from "@/lib/roleColors";
import { AdminTaskCategorySelect } from "./AdminTaskCategorySelect";

interface AdminTaskCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTaskCreated?: () => void;
  preselectedUserId?: string;
}

interface UserWithUNA {
  id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
  una_id?: string;
  una_name?: string;
  source?: 'database' | 'ontraport';
  ontraport_id?: string;
  stage?: string;
  role?: string;
}



export function AdminTaskCreationModal({
  isOpen,
  onClose,
  onTaskCreated,
  preselectedUserId,
}: AdminTaskCreationModalProps) {
  const queryClient = useQueryClient();
  const { admin } = useCurrentAdmin();

  // Form state
  const [selectedUser, setSelectedUser] = useState<UserWithUNA | null>(null);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [taskTitle, setTaskTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [meetingDate, setMeetingDate] = useState<Date | undefined>();
  const [meetingDateOpen, setMeetingDateOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [sendNotification, setSendNotification] = useState(true);
  const [adminSectionOpen, setAdminSectionOpen] = useState(true);
  const [suggestedDueDate, setSuggestedDueDate] = useState<Date | undefined>();

  // Fetch ALL users with profiles from database
  const { data: dbUsers = [], isLoading: dbUsersLoading } = useQuery({
    queryKey: ["admin-all-profiles"],
    queryFn: async (): Promise<UserWithUNA[]> => {
      // Fetch ALL profiles
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, email, avatar_url")
        .order("full_name");

      if (profilesError) throw profilesError;
      if (!profiles || profiles.length === 0) return [];

      // Get UNA associations for display purposes
      const { data: unas } = await supabase
        .from("unas")
        .select("id, name, treasury_secretary_id");

      // Create a map of user_id -> UNA info (for Treasury Secretaries)
      const unaMap = new Map<string, { una_id: string; una_name: string }>();
      const treasurySecretaryIds = new Set<string>();
      (unas || []).forEach(una => {
        if (una.treasury_secretary_id) {
          treasurySecretaryIds.add(una.treasury_secretary_id);
          unaMap.set(una.treasury_secretary_id, {
            una_id: una.id,
            una_name: una.name || "Unknown UNA"
          });
        }
      });

      // Fetch member data including their actual role from una_members
      const { data: unaMembers } = await supabase
        .from("una_members")
        .select("user_id, member_role")
        .not("user_id", "is", null);

      // Create a map of user_id -> member_role
      const memberRoleMap = new Map<string, string>();
      (unaMembers || []).forEach(m => {
        if (m.user_id && m.member_role) {
          memberRoleMap.set(m.user_id, m.member_role);
        }
      });

      // Return all profiles with optional UNA data and role
      // Role hierarchy: Secretary Treasurer (from unas table) > actual member_role > No Role
      return profiles
        .filter(p => p.email) // Must have email for task assignment
        .map(profile => {
          const unaInfo = unaMap.get(profile.id);
          // Determine role: treasury_secretary if in treasurySecretaryIds, otherwise use actual member_role
          const role = treasurySecretaryIds.has(profile.id) 
            ? 'treasury_secretary' 
            : memberRoleMap.get(profile.id) || undefined;
          return {
            id: profile.id,
            full_name: profile.full_name || "Unknown User",
            email: profile.email || "",
            avatar_url: profile.avatar_url,
            una_id: unaInfo?.una_id,
            una_name: unaInfo?.una_name,
            source: 'database' as const,
            role,
          };
        });
    },
    enabled: isOpen,
  });

  // Fetch Ontraport contacts with completed journey
  const { data: ontraportContacts = [], isLoading: ontraportLoading } = useQuery({
    queryKey: ["ontraport-contacts"],
    queryFn: async (): Promise<UserWithUNA[]> => {
      const { data, error } = await supabase.functions.invoke('fetch-ontraport-contacts');
      
      if (error) {
        console.error('Error fetching Ontraport contacts:', error);
        return [];
      }
      
      if (!data?.success || !data?.contacts) {
        return [];
      }

      // Transform Ontraport contacts to UserWithUNA format
      return data.contacts.map((contact: any) => ({
        id: `ontraport-${contact.ontraport_id}`,
        full_name: contact.full_name,
        email: contact.email,
        avatar_url: null,
        una_id: '',
        una_name: '',
        source: 'ontraport' as const,
        ontraport_id: contact.ontraport_id,
        stage: contact.stage,
      }));
    },
    enabled: isOpen,
  });

  // Merge and deduplicate users - prefer database users over Ontraport
  const users = useMemo(() => {
    const dbEmails = new Set(dbUsers.map(u => u.email.toLowerCase()));
    const uniqueOntraport = ontraportContacts.filter(
      c => !dbEmails.has(c.email.toLowerCase())
    );
    return [...dbUsers, ...uniqueOntraport];
  }, [dbUsers, ontraportContacts]);

  const usersLoading = dbUsersLoading || ontraportLoading;

  // Handle preselected user
  useEffect(() => {
    if (preselectedUserId && users.length > 0) {
      const user = users.find((u) => u.id === preselectedUserId);
      if (user) setSelectedUser(user);
    }
  }, [preselectedUserId, users]);

  // Create task mutation
  const createTaskMutation = useMutation({
    mutationFn: async (createAnother: boolean) => {
      if (!selectedUser || !taskTitle.trim()) {
        throw new Error("Please select a member and enter a task title");
      }

      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error("Not authenticated");

      // For Ontraport-only contacts, just send email notification
      if (selectedUser.source === 'ontraport') {
        if (sendNotification) {
          await sendAdminTaskAssignedEmail({
            recipientEmail: selectedUser.email,
            recipientName: selectedUser.full_name,
            taskTitle: taskTitle.trim(),
            taskDescription: description?.trim(),
            dueDate: dueDate ? format(dueDate, "yyyy-MM-dd") : null,
            category,
            taskId: null, // No task ID for Ontraport-only
          });
        }
        return { task: null, createAnother, isOntraportOnly: true };
      }

      // Insert task for database users
      const { data: task, error: taskError } = await supabase
        .from("custom_tasks")
        .insert({
          user_id: selectedUser.id,
          una_id: selectedUser.una_id || null,
          task_title: taskTitle.trim(),
          task_description: description?.trim() || null,
          category_type: category,
          due_date: dueDate ? format(dueDate, "yyyy-MM-dd") : null,
          status: "not_started",
          created_by_admin_id: authUser.id,
          is_admin_assigned: true,
          is_from_workflow: false,
          source_meeting_date: meetingDate ? format(meetingDate, "yyyy-MM-dd") : null,
          admin_notes: adminNotes?.trim() || null,
          privacy: "all_members",
        })
        .select()
        .single();

      if (taskError) throw taskError;

      // Send email notification
      if (sendNotification && task) {
        await sendAdminTaskAssignedEmail({
          recipientEmail: selectedUser.email,
          recipientName: selectedUser.full_name,
          taskTitle: taskTitle.trim(),
          taskDescription: description?.trim(),
          dueDate: dueDate ? format(dueDate, "yyyy-MM-dd") : null,
          category,
          taskId: task.id,
        });
      }

      return { task, createAnother, isOntraportOnly: false };
    },
    onSuccess: ({ task, createAnother, isOntraportOnly }) => {
      if (isOntraportOnly) {
        toast.success(`Task email sent to ${selectedUser?.full_name} (Ontraport contact)`);
      } else {
        toast.success(`Task created for ${selectedUser?.full_name}`);
      }
      queryClient.invalidateQueries({ queryKey: ["admin-tasks"] });
      onTaskCreated?.();

      if (createAnother) {
        // Reset form but keep user selected
        setSelectedTemplateId("");
        setTaskTitle("");
        setDescription("");
        setCategory("other");
    setDueDate(undefined);
    setSuggestedDueDate(undefined);
    setMeetingDate(undefined);
        setAdminNotes("");
      } else {
        onClose();
        resetForm();
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const resetForm = () => {
    setSelectedUser(null);
    setSelectedTemplateId("");
    setTaskTitle("");
    setDescription("");
        setCategory("other");
        setDueDate(undefined);
        setSuggestedDueDate(undefined);
        setMeetingDate(undefined);
    setAdminNotes("");
    setSendNotification(true);
  };

  const isFormValid = selectedUser && taskTitle.trim();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Task for Member</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Section 1: Select Member */}
          <div className="space-y-2">
            <Label>Select Member *</Label>
            <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between"
                >
                  {selectedUser ? (
                    <span className="truncate">
                      {selectedUser.full_name} - {selectedUser.una_name}
                    </span>
                  ) : (
                    "Search for a member..."
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command className="overflow-visible">
                  <CommandInput placeholder="Search by name, email, or UNA..." />
                  <CommandList className="max-h-[300px]">
                    <CommandEmpty>
                      {usersLoading ? "Loading..." : "No members found."}
                    </CommandEmpty>
                    <ScrollArea className="h-[250px]">
                      <CommandGroup className="overflow-visible">
                        {users.map((user) => (
                        <CommandItem
                          key={user.id}
                          value={`${user.full_name} ${user.email} ${user.una_name} ${user.stage || ''}`}
                          onSelect={() => {
                            setSelectedUser(user);
                            setUserSearchOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4 flex-shrink-0",
                              selectedUser?.id === user.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div className="flex flex-col flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium truncate">
                                {user.full_name}
                                {user.una_name && ` - ${user.una_name}`}
                              </span>
                              {user.source === 'ontraport' ? (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 flex-shrink-0">
                                  Ontraport
                                </Badge>
                              ) : user.role ? (
                                <Badge 
                                  variant="outline" 
                                  className="text-xs flex-shrink-0"
                                  style={{
                                    backgroundColor: getRoleColor(user.role).bg,
                                    color: getRoleColor(user.role).text,
                                    borderColor: getRoleColor(user.role).border,
                                  }}
                                >
                                  {getRoleColor(user.role).name}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs bg-muted text-muted-foreground border-border flex-shrink-0">
                                  No Role
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="truncate">{user.email}</span>
                              {user.stage && (
                                <span className="text-xs text-amber-600">• {user.stage}</span>
                              )}
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                      </CommandGroup>
                    </ScrollArea>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {selectedUser && (
              <div className="mt-3 p-4 rounded-lg border bg-muted/30">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={selectedUser.avatar_url || undefined} />
                    <AvatarFallback>
                      {selectedUser.full_name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{selectedUser.full_name}</p>
                      {selectedUser.source === 'ontraport' ? (
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                          Ontraport Only
                        </Badge>
                      ) : selectedUser.role ? (
                        <Badge 
                          variant="outline" 
                          className="text-xs"
                          style={{
                            backgroundColor: getRoleColor(selectedUser.role).bg,
                            color: getRoleColor(selectedUser.role).text,
                            borderColor: getRoleColor(selectedUser.role).border,
                          }}
                        >
                          {getRoleColor(selectedUser.role).name}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-muted text-muted-foreground border-border">
                          No Role
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{selectedUser.email}</p>
                    {selectedUser.stage && (
                      <p className="text-xs text-amber-600 mt-1">Journey Stage: {selectedUser.stage}</p>
                    )}
                  </div>
                  {selectedUser.una_name && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Building2 className="h-4 w-4" />
                      <span className="truncate max-w-[150px]">{selectedUser.una_name}</span>
                    </div>
                  )}
                </div>
                {selectedUser.source === 'ontraport' && (
                  <p className="text-xs text-muted-foreground mt-3 p-2 bg-amber-50 rounded border border-amber-200">
                    Note: This contact doesn't have a platform account yet. Task will be sent via email.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Section 3: Task Details */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Task Title *</Label>
              <Input
                id="title"
                placeholder="Enter task title"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe what needs to be done..."
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <AdminTaskCategorySelect value={category} onChange={setCategory} />
            </div>

            {/* Due Date */}
            <div className="space-y-2">
              <Label>Due Date</Label>
              <div className="flex gap-2">
                <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "flex-1 justify-start text-left font-normal",
                        !dueDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dueDate ? format(dueDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={(date) => {
                        setDueDate(date);
                        setDueDateOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {dueDate && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDueDate(undefined)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {suggestedDueDate && (
                <p className="text-sm text-muted-foreground">
                  Suggested: {format(suggestedDueDate, "PPP")} (from template)
                </p>
              )}
            </div>
          </div>

          {/* Section 4: Admin Information */}
          <Collapsible open={adminSectionOpen} onOpenChange={setAdminSectionOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between p-0 h-auto">
                <span className="font-medium">Admin Information</span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform",
                    adminSectionOpen && "rotate-180"
                  )}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-4 mt-4 p-4 rounded-lg bg-muted/50">
                <div className="space-y-2">
                  <Label>Meeting/Strategy Session Date</Label>
                  <Popover open={meetingDateOpen} onOpenChange={setMeetingDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !meetingDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {meetingDate ? format(meetingDate, "PPP") : "When did you discuss this?"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={meetingDate}
                        onSelect={(date) => {
                          setMeetingDate(date);
                          setMeetingDateOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminNotes">Admin Notes</Label>
                  <Textarea
                    id="adminNotes"
                    placeholder="Notes about this task assignment..."
                    rows={3}
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Internal only - not visible to member
                  </p>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Section 5: Notification */}
          <div className="flex items-start space-x-3 p-4 rounded-lg border">
            <Checkbox
              id="sendNotification"
              checked={sendNotification}
              onCheckedChange={(checked) => setSendNotification(checked as boolean)}
            />
            <div className="space-y-1">
              <Label htmlFor="sendNotification" className="cursor-pointer">
                Send email notification to member
              </Label>
              <p className="text-sm text-muted-foreground">
                Member will receive an email about this new task
              </p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => createTaskMutation.mutate(true)}
            disabled={!isFormValid || createTaskMutation.isPending}
          >
            {createTaskMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Create & Add Another
          </Button>
          <Button
            onClick={() => createTaskMutation.mutate(false)}
            disabled={!isFormValid || createTaskMutation.isPending}
          >
            {createTaskMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Create Task
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
